import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON
} from "@simplewebauthn/browser";
import { isRecord, requestJson } from "./http.js";

export type TotpState = "disabled" | "pending" | "enabled";

export interface TwoFactorStatus {
  readonly totp: TotpState;
  readonly webauthnKeys: number;
  readonly backupCodesRemaining: number;
}

export interface TotpSetup {
  readonly otpauthUrl: string;
  readonly manualKey: string;
}

export interface PasskeyMeta {
  readonly id: string;
  readonly nickname: string;
  readonly createdAt: number;
  /** Set while this passkey can unlock the vault without the master password. */
  readonly unlockEnabledAt: number | null;
}

export function isTwoFactorStatus(value: unknown): value is TwoFactorStatus {
  return isRecord(value) &&
    (value["totp"] === "disabled" || value["totp"] === "pending" || value["totp"] === "enabled") &&
    typeof value["webauthnKeys"] === "number" &&
    typeof value["backupCodesRemaining"] === "number";
}

export function isWebauthnCredentialMeta(value: unknown): value is Omit<PasskeyMeta, "unlockEnabledAt"> {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["nickname"] === "string" &&
    typeof value["createdAt"] === "number";
}

/** A passkey from the server; servers without passkey unlock send no unlock field. */
export function parsePasskeyMeta(value: unknown): PasskeyMeta | undefined {
  if (!isRecord(value) || !isWebauthnCredentialMeta(value)) {
    return undefined;
  }
  const unlock: unknown = "unlockEnabledAt" in value ? value.unlockEnabledAt : null;
  return { id: value.id, nickname: value.nickname, createdAt: value.createdAt, unlockEnabledAt: typeof unlock === "number" ? unlock : null };
}

function isTotpSetup(value: unknown): value is TotpSetup {
  return isRecord(value) && typeof value["otpauthUrl"] === "string" && typeof value["manualKey"] === "string";
}

function isCreationOptions(value: unknown): value is PublicKeyCredentialCreationOptionsJSON {
  return isRecord(value) && typeof value["challenge"] === "string" && isRecord(value["rp"]);
}

function isRequestOptions(value: unknown): value is PublicKeyCredentialRequestOptionsJSON {
  return isRecord(value) && typeof value["challenge"] === "string";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

export async function fetchTwoFactorStatus(): Promise<TwoFactorStatus> {
  const body = await requestJson("/api/v1/2fa/status");
  if (!isTwoFactorStatus(body)) {
    throw unexpected();
  }
  return body;
}

export async function startTotpSetup(): Promise<TotpSetup> {
  const body = await requestJson("/api/v1/2fa/totp/setup", { method: "POST", body: {} });
  if (!isTotpSetup(body)) {
    throw unexpected();
  }
  return body;
}

export async function verifyTotpSetup(code: string): Promise<void> {
  await requestJson("/api/v1/2fa/totp/verify", { method: "POST", body: { code } });
}

export async function disableTotp(proof: { readonly code: string } | { readonly backupCode: string }): Promise<void> {
  await requestJson("/api/v1/2fa/totp/disable", { method: "POST", body: proof });
}

export async function regenerateBackupCodes(): Promise<string[]> {
  const body = await requestJson("/api/v1/2fa/backup-codes/regenerate", { method: "POST", body: {} });
  if (!isRecord(body) || !Array.isArray(body["codes"])) {
    throw unexpected();
  }
  const codes: string[] = [];
  for (const code of body["codes"]) {
    if (typeof code !== "string") {
      throw unexpected();
    }
    codes.push(code);
  }
  return codes;
}

export async function listPasskeys(): Promise<PasskeyMeta[]> {
  const body = await requestJson("/api/v1/webauthn/credentials");
  if (!isRecord(body) || !Array.isArray(body["credentials"])) {
    throw unexpected();
  }
  const out: PasskeyMeta[] = [];
  for (const entry of body["credentials"]) {
    const parsed = parsePasskeyMeta(entry);
    if (parsed === undefined) {
      throw unexpected();
    }
    out.push(parsed);
  }
  return out;
}

export async function passkeyRegistrationOptions(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  const body = await requestJson("/api/v1/webauthn/register/options", { method: "POST", body: {} });
  if (!isCreationOptions(body)) {
    throw unexpected();
  }
  return body;
}

export async function verifyPasskeyRegistration(
  nickname: string,
  response: unknown
): Promise<{ readonly id: string; readonly nickname: string }> {
  const body = await requestJson("/api/v1/webauthn/register/verify", {
    method: "POST",
    body: { nickname, response }
  });
  if (!isRecord(body) || typeof body["id"] !== "string" || typeof body["nickname"] !== "string") {
    throw unexpected();
  }
  return { id: body["id"], nickname: body["nickname"] };
}

export async function deletePasskey(id: string): Promise<void> {
  await requestJson(`/api/v1/webauthn/credentials/${id}`, { method: "DELETE" });
}

/** Passkey unlock, step 1: an assertion request for this one passkey. */
export async function passkeyUnlockSetupOptions(id: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const body = await requestJson(`/api/v1/webauthn/credentials/${id}/unlock/options`, { method: "POST", body: {} });
  if (!isRequestOptions(body)) {
    throw unexpected();
  }
  return body;
}

/** Passkey unlock, step 2: the account keys sealed under the passkey's PRF key, with the assertion as proof. */
export async function enablePasskeyUnlock(id: string, keyEncB64: string, response: unknown): Promise<void> {
  await requestJson(`/api/v1/webauthn/credentials/${id}/unlock`, { method: "PUT", body: { keyEncB64, response } });
}

export async function disablePasskeyUnlock(id: string): Promise<void> {
  await requestJson(`/api/v1/webauthn/credentials/${id}/unlock`, { method: "DELETE" });
}
